/*
 * Valoraa product page helpers.
 * Keeps the "Save ₹X" badge (blocks/vb-product-price.liquid) and the low-stock line
 * (blocks/vb-product-stock.liquid) in step with the selected variant. Horizon's variant picker
 * fires the standard product-select event with a promise that resolves to the re-rendered
 * section HTML; we copy our nodes from it, matched by block id, the same way product-price does.
 * Also keeps the hidden sticky add-to-cart bar inert (see bindStickyBars) and shows the bar while
 * the main buy buttons are off screen in either direction (see controlStickyBar), and adds
 * arrow-key navigation to the gallery thumbnails (see bindThumbnailKeys), mirrors the current
 * thumbnail as aria-current (see mirrorThumbCurrent) and makes the gallery scroller a labelled,
 * focusable region that opens zoom on Enter (see labelGallery).
 */
import { StandardEvents } from '@shopify/events';

const SELECTORS = ['[data-vb-save]', '[data-vb-stock]'];
const bound = new WeakSet();

/**
 * @param {Element} section
 */
function bind(section) {
  if (bound.has(section)) return;
  bound.add(section);
  section.addEventListener(StandardEvents.productSelect, (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    /** @type {Promise<{detail?: {html?: Document}}> | undefined} */
    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;
    promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        for (const selector of SELECTORS) {
          for (const current of section.querySelectorAll(selector)) {
            const attr = selector.slice(1, -1);
            const next = html.querySelector(`[${attr}="${current.getAttribute(attr)}"]`);
            if (next) current.replaceWith(next.cloneNode(true));
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[vb-product]', error);
      });
  });
}

/**
 * Sticky add-to-cart bar (Horizon's sticky-add-to-cart, JS untouched): while the bar is not shown,
 * keep it out of the tab order and the accessibility tree with `inert`. Horizon toggles
 * data-stuck="true|false" on the bar, and hides it entirely when the variant is unavailable
 * (data-variant-available on the host), so we mirror those two attributes.
 */
const stickyObserved = new WeakSet();

/**
 * @param {Element} bar
 */
function syncStickyInert(bar) {
  const host = bar.closest('sticky-add-to-cart');
  const shown = bar.getAttribute('data-stuck') === 'true' && host?.getAttribute('data-variant-available') === 'true';
  if (bar instanceof HTMLElement) bar.inert = !shown;
}

function bindStickyBars() {
  for (const bar of document.querySelectorAll('sticky-add-to-cart .sticky-add-to-cart__bar')) {
    syncStickyInert(bar);
    if (stickyObserved.has(bar)) continue;
    stickyObserved.add(bar);
    const observer = new MutationObserver(() => syncStickyInert(bar));
    observer.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    const host = bar.closest('sticky-add-to-cart');
    if (host) observer.observe(host, { attributes: true, attributeFilter: ['data-variant-available'] });
  }
}

/**
 * Sticky bar visibility. Horizon only shows the bar once the main buy buttons have scrolled above
 * the viewport, so the first mobile screen has no add-to-cart. Atolea shows it straight away, so we
 * also show it while the buy buttons are still below the fold: the bar is up whenever the main buy
 * buttons are off screen (either direction) and hidden while they, or the footer, are on screen.
 * Horizon keeps writing data-stuck from its own observers; we re-assert our state after each write.
 */
const stickyControlled = new WeakSet();

/**
 * The footer the bar should give way to: the last section of the footer group (the links and
 * newsletter block). The trust strip that opens the footer group is only an icon row, so the bar
 * stays up over it, and the shopper keeps an add-to-cart in reach through the recommendations
 * and the FAQ, right until the real footer arrives.
 * @returns {Element | null}
 */
function mainFooter() {
  const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  if (!footer) return null;
  const sections = footer.querySelectorAll(':scope > .shopify-section, :scope .shopify-section');
  return sections.length ? sections[sections.length - 1] : footer;
}

function isChatActive() {
  return Boolean(customElements.get('shopify-chat') && document.querySelector('shopify-chat'));
}

/**
 * @param {Element} host
 */
function controlStickyBar(host) {
  if (stickyControlled.has(host)) return;
  const bar = host.querySelector('.sticky-add-to-cart__bar');
  const buyButtons = host.closest('.shopify-section')?.querySelector('.buy-buttons-block');
  if (!bar || !buyButtons) return;
  stickyControlled.add(host);

  const footer = mainFooter();
  const state = { buyButtons: true, footer: false };
  const apply = () => {
    const wanted = !state.buyButtons && !state.footer && !isChatActive() ? 'true' : 'false';
    if (bar.getAttribute('data-stuck') !== wanted) bar.setAttribute('data-stuck', wanted);
  };

  new IntersectionObserver(([entry]) => {
    if (!entry) return;
    state.buyButtons = entry.isIntersecting;
    apply();
  }).observe(buyButtons);

  if (footer) {
    new IntersectionObserver(([entry]) => {
      if (!entry) return;
      state.footer = entry.isIntersecting;
      apply();
    }).observe(footer);
  }

  new MutationObserver(apply).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
}

/**
 * Gallery thumbnails (Horizon's slideshow-controls, markup untouched): arrow keys move between
 * thumbnails and select the matching slide, Home/End jump to the first/last. Each thumbnail stays
 * a normal tab stop, so Tab and Enter keep working as before. Delegated once on the document so it
 * survives section re-renders and variant changes.
 */
const THUMB = '.product-information .slideshow-controls__thumbnail';
const THUMB_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);
let thumbKeysBound = false;

function bindThumbnailKeys() {
  if (thumbKeysBound) return;
  thumbKeysBound = true;
  document.addEventListener('keydown', (event) => {
    if (!THUMB_KEYS.has(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
    const current = event.target instanceof Element ? event.target.closest(THUMB) : null;
    if (!(current instanceof HTMLElement)) return;
    const list = current.parentElement;
    if (!list) return;
    const thumbs = /** @type {HTMLElement[]} */ (
      [...list.querySelectorAll(':scope > .slideshow-controls__thumbnail')].filter(
        (el) => el instanceof HTMLElement && el.offsetParent !== null
      )
    );
    const index = thumbs.indexOf(current);
    if (index < 0 || thumbs.length < 2) return;
    let next = index;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = thumbs.length - 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + thumbs.length) % thumbs.length;
    else next = (index + 1) % thumbs.length;
    event.preventDefault();
    const target = thumbs[next];
    if (!target) return;
    target.focus({ preventScroll: true });
    target.click();
  });
}

/**
 * Gallery scroller (Horizon's slideshow-slides, tabindex -1): axe's scrollable-region-focusable.
 * Make the product page's scroller a labelled, focusable region so keyboard users can scroll it
 * with the arrow keys. When zoom is on (slides carry Horizon's on:click zoom action), Enter or
 * Space on the focused region opens the zoom dialog on the current slide, the same as a click.
 * The labels come from the price block (translated in Liquid).
 */
const GALLERY = '.product-information .media-gallery--carousel slideshow-slides';
let galleryKeysBound = false;

/**
 * @param {Element} scroller
 * @returns {HTMLElement | null}
 */
function zoomableSlide(scroller) {
  const slide =
    scroller.querySelector(':scope > slideshow-slide[aria-hidden="false"]') ??
    scroller.querySelector(':scope > slideshow-slide');
  if (!(slide instanceof HTMLElement)) return null;
  return (slide.getAttribute('on:click') ?? '').includes('zoom-dialog') ? slide : null;
}

function labelGallery() {
  const source = document.querySelector('[data-vb-gallery-label]');
  const label = source?.getAttribute('data-vb-gallery-label');
  if (!label) return;
  const zoomLabel = source?.getAttribute('data-vb-gallery-label-zoom') || label;
  for (const scroller of document.querySelectorAll(GALLERY)) {
    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-label', zoomableSlide(scroller) ? zoomLabel : label);
  }

  if (galleryKeysBound) return;
  galleryKeysBound = true;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;
    const scroller = event.target;
    if (!(scroller instanceof Element) || !scroller.matches(GALLERY)) return;
    const slide = zoomableSlide(scroller);
    if (!slide) return;
    event.preventDefault();
    slide.click();
  });
}

/**
 * Gallery thumbnails: Horizon marks the current one with aria-selected on a plain <button> (no
 * tab/listbox role, so screen readers ignore it). Mirror it as aria-current, which a button may
 * carry, so the current image is announced. Horizon's own attribute and styling stay as they are.
 */
const thumbsObserved = new WeakSet();

/**
 * @param {Element} thumb
 */
function syncThumbCurrent(thumb) {
  if (thumb.getAttribute('aria-selected') === 'true') thumb.setAttribute('aria-current', 'true');
  else thumb.removeAttribute('aria-current');
}

function mirrorThumbCurrent() {
  for (const thumb of document.querySelectorAll(THUMB)) {
    syncThumbCurrent(thumb);
    if (thumbsObserved.has(thumb)) continue;
    thumbsObserved.add(thumb);
    new MutationObserver(() => syncThumbCurrent(thumb)).observe(thumb, {
      attributes: true,
      attributeFilter: ['aria-selected'],
    });
  }
}

function init() {
  for (const node of document.querySelectorAll(SELECTORS.join(','))) {
    const section = node.closest('.shopify-section, dialog');
    if (section) bind(section);
  }
  bindStickyBars();
  bindThumbnailKeys();
  labelGallery();
  mirrorThumbCurrent();
  for (const host of document.querySelectorAll('sticky-add-to-cart')) controlStickyBar(host);
}

init();
document.addEventListener('shopify:section:load', init);


/* ---------- Perf: warm the rest of the gallery after load ----------
   Gallery media 3+ render with loading="lazy" (snippets/product-media-gallery-content) so they
   don't compete with the LCP photo. Once the page has loaded and the main thread is idle, only
   the next unloaded photo is switched to eager (skipped on data saver / 2G / 3G). The rest are
   warmed on the shopper's first interaction with the gallery (swipe, scroll, tap, thumbnail or
   key), so swiping never lands on an empty slide but a shopper who never swipes doesn't pay for
   every photo. The zoom dialog's full-size copies stay lazy (they only load when zoom opens). */
const GALLERY_LAZY = '.product-information media-gallery img.product-media__image[loading="lazy"]';

/** @param {number} [limit] */
function warmGalleryImages(limit = Infinity) {
  let warmed = 0;
  for (const image of document.querySelectorAll(GALLERY_LAZY)) {
    if (warmed >= limit) break;
    if (!(image instanceof HTMLImageElement) || image.closest('.dialog-zoomed-gallery')) continue;
    image.loading = 'eager';
    warmed += 1;
  }
}

/** @param {Event} event */
function onGalleryInteraction(event) {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest('.product-information media-gallery')) return;
  if (target.closest('.dialog-zoomed-gallery')) return;
  warmGalleryImages();
  for (const type of ['pointerdown', 'keydown', 'scroll']) {
    document.removeEventListener(type, onGalleryInteraction, { capture: true });
  }
}

function warmGallery() {
  if (!document.querySelector(GALLERY_LAZY)) return;
  for (const type of ['pointerdown', 'keydown', 'scroll']) {
    document.addEventListener(type, onGalleryInteraction, { capture: true, passive: true });
  }
  const connection = /** @type {any} */ (navigator).connection;
  if (connection?.saveData || /(^|-)2g$|^3g$/.test(connection?.effectiveType || '')) return;
  const run = () => warmGalleryImages(1);
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 4000 });
  else setTimeout(run, 2000);
}

if (document.readyState === 'complete') warmGallery();
else window.addEventListener('load', warmGallery, { once: true });
